import * as THREE from "three"
import type { SceneThemeId } from "@/lib/location-scene-3d/scene-state"

export type ViewCubeViewId = "top" | "front" | "back" | "left" | "right" | "iso"

export type ViewCubeVec3 = [number, number, number]

export type ViewCubeLabels = Record<Exclude<ViewCubeViewId, "iso">, string>

export type ViewCubeHandlers = {
  onViewSelect?: (viewId: ViewCubeViewId) => void
  onOrbitStart?: () => void
  onOrbit?: (dx: number, dy: number) => void
  onOrbitEnd?: () => void
}

export type ViewCubeOptions = ViewCubeHandlers & {
  labels?: Partial<ViewCubeLabels>
  theme?: SceneThemeId
  activeView?: ViewCubeViewId | null
  orientation?: { direction: ViewCubeVec3; up: ViewCubeVec3 } | null
}

type ViewCubeColors = {
  face: string
  edge: string
  text: string
  hoverFace: string
  hoverEdge: string
  hoverText: string
  axisX: string
  axisY: string
  axisZ: string
}

const VIEWS: readonly ViewCubeViewId[] = ["top", "front", "back", "left", "right", "iso"]

const DEFAULT_LABELS: ViewCubeLabels = {
  top: "Top",
  front: "Front",
  back: "Back",
  left: "Left",
  right: "Right",
}

const FRAME = { width: 170, height: 140 }
const SIZE = { width: 153, height: 124 }
const CROP = { x: 17, y: 0 }
const CUBE = { half: 0.8, fov: 22, distance: 11.25, aimZ: -0.3 }
const FACE_TEXTURE = { size: 256, edge: 1.5, font: "48px sans-serif", textY: 130, anisotropy: 4 }
const AXES = {
  lengthX: 2.2,
  lengthY: 2.2,
  lengthZ: 2.0,
  headLength: 0.22,
  headWidth: 0.13,
  labelSize: 0.45,
  labelGap: 0.32,
}
const CLICK_THRESHOLD = { mouse: 4, touch: 8 }

/** BoxGeometry groups (internal Y-up): +X, -X, +Y, -Y, +Z, -Z → Right, Left, Top, bottom, Front, Back. */
const FACE_VIEWS: readonly (Exclude<ViewCubeViewId, "iso"> | null)[] = ["right", "left", "top", null, "front", "back"]
const FACE_SHADE = [0.86, 0.86, 1.0, 0.8, 0.93, 0.93]

const VIEW_ORIENTATION: Record<ViewCubeViewId, { direction: ViewCubeVec3; up: ViewCubeVec3 }> = {
  top: { direction: [0, 0, 1], up: [0, 1, 0] },
  front: { direction: [0, -1, 0], up: [0, 0, 1] },
  back: { direction: [0, 1, 0], up: [0, 0, 1] },
  left: { direction: [-1, 0, 0], up: [0, 0, 1] },
  right: { direction: [1, 0, 0], up: [0, 0, 1] },
  iso: {
    direction: [-1 / Math.sqrt(3), -1 / Math.sqrt(3), 1 / Math.sqrt(3)],
    up: [0, 0, 1],
  },
}

const DAY_CUBE_TOKENS = {
  face: "--clay-0",
  edge: "--text-muted",
  text: "--text-display",
  hoverFace: "--terra-3",
  hoverEdge: "--terra-5",
  hoverText: "--text-on-accent",
  axisX: "--danger",
  axisY: "--success",
  axisZ: "--river-5",
} as const

const NIGHT_CUBE_TOKENS = {
  face: "--scene-night-block-top",
  edge: "--scene-night-block-grid",
  text: "--scene-cube-night-text",
  hoverFace: "--scene-cube-night-hover-face",
  hoverEdge: "--scene-cube-night-hover-edge",
  hoverText: "--scene-cube-night-text",
  axisX: "--scene-cube-night-axis-x",
  axisY: "--scene-cube-night-axis-y",
  axisZ: "--scene-cube-night-axis-z",
} as const

const CUBE_TOKENS: Record<SceneThemeId, { [K in keyof ViewCubeColors]: string }> = {
  light: DAY_CUBE_TOKENS,
  dark: NIGHT_CUBE_TOKENS,
}

type FaceView = Exclude<ViewCubeViewId, "iso"> | null

type Gesture = {
  id: number
  x: number
  y: number
  lastX: number
  lastY: number
  face: number
  dragging: boolean
  threshold: number
}

function toInternal(v: ViewCubeVec3 | THREE.Vector3): THREE.Vector3 {
  const x = Array.isArray(v) ? v[0] : v.x
  const y = Array.isArray(v) ? v[1] : v.y
  const z = Array.isArray(v) ? v[2] : v.z
  return new THREE.Vector3(x, z, -y)
}

function isViewId(value: string | null): value is ViewCubeViewId {
  return value != null && (VIEWS as readonly string[]).includes(value)
}

function resolveToken(root: HTMLElement, token: string): string {
  const probe = document.createElement("span")
  probe.style.backgroundColor = `var(${token})`
  root.appendChild(probe)
  const color = getComputedStyle(probe).backgroundColor
  probe.remove()
  return color
}

function readColors(root: HTMLElement, theme: SceneThemeId): ViewCubeColors {
  const tokens = CUBE_TOKENS[theme]
  const read = (token: string) => resolveToken(root, token)
  const appLight = document.documentElement.classList.contains("light")
  return {
    face: read(tokens.face),
    edge: read(tokens.edge),
    text: read(tokens.text),
    hoverFace: read(appLight ? tokens.hoverFace : "--ui-accent"),
    hoverEdge: read(appLight ? tokens.hoverEdge : "--ui-accent-press"),
    hoverText: read(appLight ? tokens.hoverText : "--ui-on-accent"),
    axisX: read(tokens.axisX),
    axisY: read(tokens.axisY),
    axisZ: read(tokens.axisZ),
  }
}

/**
 * Self-contained navigation cube. It does not know about the scene camera.
 * Orientation is Z-up. The camera sits on the ray through the aim point, so an axis face stays a rectangle.
 */
export class ViewCube {
  static readonly layout = SIZE

  private readonly labels: ViewCubeLabels
  private handlers: ViewCubeHandlers
  private skin: SceneThemeId
  private colors: ViewCubeColors
  private colorKey = ""
  private activeView: ViewCubeViewId | null
  private hoverFace = -1
  private gesture: Gesture | null = null
  private disposed = false

  private readonly root: HTMLDivElement
  private readonly canvasWrap: HTMLDivElement
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.PerspectiveCamera
  private readonly aim: THREE.Vector3
  private readonly cube: THREE.Mesh
  private readonly materials: THREE.MeshBasicMaterial[]
  private readonly faceTextures: { normal: THREE.CanvasTexture; hover: THREE.CanvasTexture | null }[]
  private readonly textures: THREE.CanvasTexture[] = []
  private readonly axisHelpers: THREE.ArrowHelper[]
  private readonly axisSprites: THREE.Sprite[]
  private readonly raycaster = new THREE.Raycaster()
  private readonly unbind: Array<() => void> = []

  constructor(container: HTMLElement, options: ViewCubeOptions = {}) {
    this.labels = { ...DEFAULT_LABELS, ...options.labels }
    this.skin = options.theme === "dark" ? "dark" : "light"
    this.handlers = {
      onViewSelect: options.onViewSelect,
      onOrbitStart: options.onOrbitStart,
      onOrbit: options.onOrbit,
      onOrbitEnd: options.onOrbitEnd,
    }
    this.activeView = options.activeView === undefined ? "iso" : options.activeView

    this.root = document.createElement("div")
    Object.assign(this.root.style, {
      position: "relative",
      width: `${SIZE.width}px`,
      height: `${SIZE.height}px`,
      userSelect: "none",
    })

    this.canvasWrap = document.createElement("div")
    Object.assign(this.canvasWrap.style, {
      width: `${SIZE.width}px`,
      height: `${SIZE.height}px`,
      touchAction: "none",
    })

    this.root.append(this.canvasWrap)
    container.appendChild(this.root)
    this.colors = readColors(this.root, this.skin)
    this.colorKey = JSON.stringify(this.colors)

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(SIZE.width, SIZE.height)
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.domElement.style.display = "block"
    this.canvasWrap.appendChild(this.renderer.domElement)

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(CUBE.fov, FRAME.width / FRAME.height, 0.1, 40)
    this.camera.setViewOffset(FRAME.width, FRAME.height, CROP.x, CROP.y, SIZE.width, SIZE.height)
    this.aim = toInternal([0, 0, CUBE.aimZ])

    this.faceTextures = FACE_VIEWS.map((view) => {
      const label = view ? this.labels[view] : ""
      const normal = this.makeFaceTexture(label, false)
      const hover = view ? this.makeFaceTexture(label, true) : null
      this.textures.push(normal)
      if (hover) this.textures.push(hover)
      return { normal, hover }
    })
    this.materials = FACE_SHADE.map(
      (shade, index) =>
        new THREE.MeshBasicMaterial({
          map: this.faceTextures[index].normal,
          color: new THREE.Color(shade, shade, shade),
          transparent: false,
          opacity: 1,
          depthWrite: true,
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 1,
        }),
    )
    this.cube = new THREE.Mesh(new THREE.BoxGeometry(CUBE.half * 2, CUBE.half * 2, CUBE.half * 2), this.materials)
    this.scene.add(this.cube)

    const origin = toInternal([-CUBE.half, -CUBE.half, -CUBE.half])
    const axisSpecs: { dir: ViewCubeVec3; len: number; color: string; label: string }[] = [
      { dir: [1, 0, 0], len: AXES.lengthX, color: this.colors.axisX, label: "x" },
      { dir: [0, 1, 0], len: AXES.lengthY, color: this.colors.axisY, label: "y" },
      { dir: [0, 0, 1], len: AXES.lengthZ, color: this.colors.axisZ, label: "z" },
    ]
    this.axisHelpers = []
    this.axisSprites = []
    for (const axis of axisSpecs) {
      const dir = toInternal(axis.dir)
      const helper = new THREE.ArrowHelper(dir, origin, axis.len, axis.color, AXES.headLength, AXES.headWidth)
      for (const part of [helper.line, helper.cone]) part.renderOrder = 1
      this.scene.add(helper)
      this.axisHelpers.push(helper)
      const sprite = this.makeAxisLabel(axis.label, axis.color)
      sprite.position.copy(origin).addScaledVector(dir, axis.len + AXES.labelGap)
      this.scene.add(sprite)
      this.axisSprites.push(sprite)
    }

    this.bindEvents()
    const initial = options.orientation ?? VIEW_ORIENTATION.iso
    this.setOrientation(initial.direction, initial.up)
  }

  setHandlers(handlers: ViewCubeHandlers) {
    this.handlers = handlers
  }

  setSkin(theme: SceneThemeId) {
    if (this.disposed) return
    const next = theme === "dark" ? "dark" : "light"
    if (next === this.skin) return
    this.skin = next
    this.refreshColors()
  }

  setOrientation(direction: ViewCubeVec3, up: ViewCubeVec3) {
    if (this.disposed) return
    const nextPos = this.cameraOnRay(direction)
    const nextUp = toInternal(up).normalize()
    if (this.camera.position.distanceToSquared(nextPos) < 1e-12 && this.camera.up.distanceToSquared(nextUp) < 1e-12) {
      return
    }
    this.camera.position.copy(nextPos)
    this.camera.up.copy(nextUp)
    this.camera.lookAt(this.aim)
    this.render()
  }

  /** `aim + normalize(direction) * distance`, so the view ray matches the face normal. */
  private cameraOnRay(direction: ViewCubeVec3): THREE.Vector3 {
    return this.aim.clone().add(toInternal(direction).normalize().multiplyScalar(CUBE.distance))
  }

  selectIso() {
    if (this.disposed) return
    this.activeView = "iso"
    this.hoverFace = -1
    this.applyFaceStates()
    this.updateCursor()
    this.render()
    this.handlers.onViewSelect?.("iso")
  }

  setActiveView(viewId: ViewCubeViewId | null) {
    if (this.disposed) return
    const next = viewId != null && isViewId(viewId) ? viewId : null
    if (next === this.activeView && !(this.hoverFace >= 0 && !this.isSelectable(this.hoverFace))) return
    this.activeView = next
    if (this.hoverFace >= 0 && !this.isSelectable(this.hoverFace)) this.hoverFace = -1
    this.applyFaceStates()
    this.updateCursor()
    this.render()
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true
    for (const off of this.unbind) off()
    for (const texture of this.textures) texture.dispose()
    for (const material of this.materials) {
      material.map = null
      material.dispose()
    }
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (mesh.geometry) mesh.geometry.dispose()
      const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : []
      for (const mat of mats) {
        if (this.materials.includes(mat as THREE.MeshBasicMaterial)) continue
        const mapped = mat as THREE.MeshBasicMaterial
        if (mapped.map) mapped.map.dispose()
        mat.dispose()
      }
    })
    this.renderer.dispose()
    this.renderer.forceContextLoss()
    this.root.remove()
  }

  private render() {
    if (!this.disposed) this.renderer.render(this.scene, this.camera)
  }

  private refreshColors() {
    if (this.disposed) return
    const next = readColors(this.root, this.skin)
    const key = JSON.stringify(next)
    if (key === this.colorKey) return
    this.colors = next
    this.colorKey = key
    const axisColors = [this.colors.axisX, this.colors.axisY, this.colors.axisZ]
    const axisLabels = ["x", "y", "z"]
    this.axisHelpers.forEach((helper, index) => helper.setColor(axisColors[index]))
    this.axisSprites.forEach((sprite, index) => {
      const material = sprite.material
      const previous = material.map
      material.map = this.makeAxisLabelTexture(axisLabels[index], axisColors[index])
      material.needsUpdate = true
      previous?.dispose()
    })
    FACE_VIEWS.forEach((view, index) => {
      const label = view ? this.labels[view] : ""
      const slot = this.faceTextures[index]
      const oldNormal = slot.normal
      const oldHover = slot.hover
      slot.normal = this.makeFaceTexture(label, false)
      slot.hover = view ? this.makeFaceTexture(label, true) : null
      this.textures.push(slot.normal)
      if (slot.hover) this.textures.push(slot.hover)
      oldNormal.dispose()
      oldHover?.dispose()
    })
    this.applyFaceStates()
    this.render()
  }

  private makeFaceTexture(label: string, hover: boolean): THREE.CanvasTexture {
    const canvas = document.createElement("canvas")
    canvas.width = FACE_TEXTURE.size
    canvas.height = FACE_TEXTURE.size
    const g = canvas.getContext("2d")
    if (!g) return new THREE.CanvasTexture(canvas)
    g.fillStyle = hover ? this.colors.hoverFace : this.colors.face
    g.fillRect(0, 0, FACE_TEXTURE.size, FACE_TEXTURE.size)
    g.strokeStyle = hover ? this.colors.hoverEdge : this.colors.edge
    g.lineWidth = FACE_TEXTURE.edge
    const inset = FACE_TEXTURE.edge / 2
    g.strokeRect(inset, inset, FACE_TEXTURE.size - FACE_TEXTURE.edge, FACE_TEXTURE.size - FACE_TEXTURE.edge)
    if (label) {
      g.fillStyle = hover ? this.colors.hoverText : this.colors.text
      g.font = FACE_TEXTURE.font
      g.textAlign = "center"
      g.textBaseline = "middle"
      g.fillText(label, FACE_TEXTURE.size / 2, FACE_TEXTURE.textY)
    }
    const pixels = g.getImageData(0, 0, FACE_TEXTURE.size, FACE_TEXTURE.size)
    for (let i = 3; i < pixels.data.length; i += 4) pixels.data[i] = 255
    g.putImageData(pixels, 0, 0)
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.anisotropy = FACE_TEXTURE.anisotropy
    return texture
  }

  private makeAxisLabelTexture(text: string, color: string): THREE.CanvasTexture {
    const canvas = document.createElement("canvas")
    canvas.width = 64
    canvas.height = 64
    const g = canvas.getContext("2d")
    if (g) {
      g.fillStyle = color
      g.font = "40px sans-serif"
      g.textAlign = "center"
      g.textBaseline = "middle"
      g.fillText(text, 32, 34)
    }
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    return texture
  }

  private makeAxisLabel(text: string, color: string): THREE.Sprite {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: this.makeAxisLabelTexture(text, color), depthTest: true, depthWrite: false }),
    )
    sprite.scale.set(AXES.labelSize, AXES.labelSize, 1)
    sprite.renderOrder = 2
    return sprite
  }

  private isSelectable(faceIndex: number): boolean {
    const view = FACE_VIEWS[faceIndex]
    return faceIndex >= 0 && view != null && view !== this.activeView
  }

  private pickFace(event: PointerEvent): number {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1,
      -((event.clientY - rect.top) / Math.max(rect.height, 1)) * 2 + 1,
    )
    this.raycaster.setFromCamera(ndc, this.camera)
    const hit = this.raycaster.intersectObject(this.cube)[0]
    return hit?.face?.materialIndex ?? -1
  }

  private applyFaceStates() {
    this.materials.forEach((material, index) => {
      const slot = this.faceTextures[index]
      const texture = index === this.hoverFace && this.isSelectable(index) && slot.hover ? slot.hover : slot.normal
      if (material.map !== texture) material.map = texture
    })
  }

  private updateCursor() {
    const canvas = this.renderer.domElement
    if (this.gesture?.dragging) {
      canvas.style.cursor = "grabbing"
      return
    }
    const index = this.hoverFace
    canvas.style.cursor = index < 0 ? "default" : this.isSelectable(index) ? "pointer" : "grab"
  }

  private setHover(index: number) {
    if (index === this.hoverFace) return
    this.hoverFace = index
    this.applyFaceStates()
    this.render()
  }

  private selectView(view: FaceView) {
    if (view == null) return
    this.activeView = view
    this.hoverFace = -1
    this.applyFaceStates()
    this.updateCursor()
    this.render()
    this.handlers.onViewSelect?.(view)
  }

  private bindEvents() {
    const themeObserver = new MutationObserver(() => this.refreshColors())
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    this.unbind.push(() => themeObserver.disconnect())

    const canvas = this.renderer.domElement
    const on = (el: EventTarget, type: string, fn: EventListener, opts?: AddEventListenerOptions) => {
      el.addEventListener(type, fn, opts)
      this.unbind.push(() => el.removeEventListener(type, fn, opts))
    }

    for (const type of ["pointerdown", "pointermove", "pointerup", "wheel", "click", "dblclick", "contextmenu"]) {
      on(this.root, type, (event) => event.stopPropagation())
    }
    on(this.root, "wheel", (event) => event.preventDefault(), { passive: false })

    on(canvas, "pointerdown", (event) => {
      const pointer = event as PointerEvent
      if (pointer.button !== 0 && pointer.pointerType === "mouse") return
      canvas.setPointerCapture(pointer.pointerId)
      this.gesture = {
        id: pointer.pointerId,
        x: pointer.clientX,
        y: pointer.clientY,
        lastX: pointer.clientX,
        lastY: pointer.clientY,
        face: this.pickFace(pointer),
        dragging: false,
        threshold: pointer.pointerType === "touch" ? CLICK_THRESHOLD.touch : CLICK_THRESHOLD.mouse,
      }
    })

    on(canvas, "pointermove", (event) => {
      const pointer = event as PointerEvent
      const gesture = this.gesture
      if (gesture && gesture.id === pointer.pointerId) {
        const moved = Math.hypot(pointer.clientX - gesture.x, pointer.clientY - gesture.y)
        if (!gesture.dragging && moved > gesture.threshold) {
          gesture.dragging = true
          this.activeView = null
          this.hoverFace = -1
          this.applyFaceStates()
          this.updateCursor()
          this.render()
          this.handlers.onOrbitStart?.()
        }
        if (gesture.dragging) {
          const dx = pointer.clientX - gesture.lastX
          const dy = pointer.clientY - gesture.lastY
          gesture.lastX = pointer.clientX
          gesture.lastY = pointer.clientY
          if (dx || dy) this.handlers.onOrbit?.(dx, dy)
        }
        return
      }
      this.setHover(this.pickFace(pointer))
      this.updateCursor()
    })

    const endGesture = (event: Event) => {
      const pointer = event as PointerEvent
      const gesture = this.gesture
      if (!gesture || gesture.id !== pointer.pointerId) return
      this.gesture = null
      if (canvas.hasPointerCapture(pointer.pointerId)) canvas.releasePointerCapture(pointer.pointerId)
      if (gesture.dragging) {
        this.handlers.onOrbitEnd?.()
      } else if (pointer.type === "pointerup" && this.isSelectable(gesture.face)) {
        this.selectView(FACE_VIEWS[gesture.face])
      }
      this.setHover(pointer.pointerType === "mouse" ? this.pickFace(pointer) : -1)
      this.applyFaceStates()
      this.updateCursor()
      this.render()
    }
    on(canvas, "pointerup", endGesture)
    on(canvas, "pointercancel", endGesture)

    on(canvas, "pointerleave", () => {
      if (this.gesture) return
      this.setHover(-1)
      this.updateCursor()
    })
  }
}
